// The stored within-generation order must not outlive a structural edit
// (TGCLOUD-1786 Phase 2, decision D5: prune at edit time, no schema change).
//
// After every settled layout the chart captures `uid -> rank` per row
// (layout.js captureEstablishedOrder) and saves it. The rank carries NO row: it
// is only meaningful among the people it was captured beside. While the arrange
// beacon is on, that order is handed to EVERY layout as a lock (Canvas.jsx), and
// the engine re-sorts each CURRENT row by stored rank (layoutV2 _applyOrderMap).
// So when an edit moves someone to another row — an FHQ "Other relative" parked
// on the top row, then attached as a cousin by Set Relation — their old top-row
// rank is read against the cousins' row and wedges them in among strangers. The
// next capture saves the wedge, so every reopen reproduces it. That is the
// ticket's "several people out of position".
//
// The cure is to forget the rank of anyone whose place in the family the edit
// changed, in the SAME state update as the edit (before the next layout can
// recapture it). They become newcomers, which the engine slots beside their
// relatives (birth order among ranked siblings — linkChildRelation now writes
// siblingIds so it can find them — else beside a ranked spouse).
//
// "Changed" is measured structurally, from the member records alone:
//   - GENERATION RELATIVE TO THE PROBAND (BFS over parent/child edges, spouses on
//     the same generation). Not absolute depth: a founder gaining parents would
//     shift everyone's depth and drop the whole family.
//   - COMPONENT. The engine top-aligns every component that is not connected to
//     the proband (Family.alignDisconnectedComponentsToTop), so for those people
//     the generation used is that TOP-ALIGNED row, measured on the proband's
//     scale; joining or leaving the proband's family is a change of its own.
//   - A NEW SIBSHIP: someone who had parents and now shares none of them (Remove
//     Child, or a re-parent), whose rank was a slot inside the old sibship.
//   - A FIRST CHILD: someone who was childless AND had no partner, and now is a
//     parent. Such a person's rank was captured with nothing to respect — in the
//     corpus copy of the ticket family (C1534) the maternal aunt, childless and
//     single while her children were still unattached, was parked between the
//     proband's parents; keeping that rank once her children are attached
//     crosses their line. A childless COUPLE is left alone: their ranks were
//     captured side by side, and dropping both leaves the married-in partner with
//     no ranked anchor — it then landed between the other partner and a co-twin
//     for no gain (TGCLOUD-1786 review). Only the new child is dropped.
// Ranks of people no longer present are dropped too. A rank whose uid is not a
// member before the edit (a proxy clone, a hidden person) is left alone.
//
// Pure and import-free. Returns the SAME Map when nothing is dropped, so a caller
// can skip the state update (and the autosave it triggers).

const idKey = (v) => (v == null ? null : String(v));

const PARENT_FIELDS = ['fatherId', 'motherId'];

// pid -> { adjacency, parents, isParent, hasPartner } over the records that
// carry a pid. `hasPartner` counts only a spouse who is a member.
const indexMembers = (members) => {
  const list = (members || []).filter((m) => m && m.pid != null);
  const byPid = new Map(list.map((m) => [idKey(m.pid), m]));
  const adjacency = new Map(list.map((m) => [idKey(m.pid), []]));
  const parents = new Map();
  const isParent = new Set();
  const hasPartner = new Set();
  // `step` is the generation of `to` minus the generation of `from`.
  const link = (from, to, step) => {
    adjacency.get(from).push([to, step]);
    adjacency.get(to).push([from, -step]);
  };
  for (const m of list) {
    const self = idKey(m.pid);
    const own = [];
    for (const field of PARENT_FIELDS) {
      const p = idKey(m[field]);
      if (p == null || p === self || !byPid.has(p) || own.includes(p)) continue;
      own.push(p);
      isParent.add(p);
      link(self, p, -1);
    }
    parents.set(self, own);
    for (const s of m.spouseIds || []) {
      const spouse = idKey(s);
      if (spouse == null || spouse === self || !byPid.has(spouse)) continue;
      link(self, spouse, 0);
      hasPartner.add(self);
      hasPartner.add(spouse);
    }
  }
  return { list, byPid, adjacency, parents, isParent, hasPartner };
};

const generationsFrom = (start, adjacency) => {
  const gen = new Map([[start, 0]]);
  const queue = [start];
  for (let i = 0; i < queue.length; i++) {
    const cur = queue[i];
    for (const [next, step] of adjacency.get(cur)) {
      if (gen.has(next)) continue;
      gen.set(next, gen.get(cur) + step);
      queue.push(next);
    }
  }
  return gen;
};

const minOf = (values) => {
  let min = Infinity;
  for (const v of values) if (v < min) min = v;
  return min;
};

/**
 * Where each member stands in the family, keyed by String(uid):
 * { component, gen, parents, isParent, hasPartner }. `component` is '@anchor' for everyone
 * connected to the anchor and '#<smallest uid>' for any other component; `gen` is
 * the generation relative to the anchor (top-aligned for other components, as
 * the engine draws them); `parents` the pids of the recorded parents present.
 * @param {object[]} members
 * @param {string|number|null} anchorUid  the proband's uid
 * @returns {Map<string, {component: string, gen: number, parents: string[], isParent: boolean, hasPartner: boolean}>}
 */
export const structuralPositions = (members, anchorUid) => {
  const { list, byPid, adjacency, parents, isParent, hasPartner } =
    indexMembers(members);
  const anchor =
    anchorUid == null
      ? null
      : list.find((m) => idKey(m.uid) === idKey(anchorUid)) || null;

  const components = [];
  const seen = new Set();
  for (const m of anchor ? [anchor, ...list] : list) {
    const pid = idKey(m.pid);
    if (seen.has(pid)) continue;
    const gen = generationsFrom(pid, adjacency);
    for (const p of gen.keys()) seen.add(p);
    components.push(gen);
  }

  const top = anchor && components.length ? minOf(components[0].values()) : 0;
  const out = new Map();
  components.forEach((gen, i) => {
    const isAnchor = !!anchor && i === 0;
    const uids = [...gen.keys()]
      .map((pid) => byPid.get(pid).uid)
      .filter((uid) => uid != null)
      .map(idKey)
      .sort();
    const component = isAnchor ? '@anchor' : `#${uids[0] ?? ''}`;
    const shift = isAnchor ? 0 : top - minOf(gen.values());
    for (const [pid, g] of gen) {
      const uid = idKey(byPid.get(pid).uid);
      if (uid == null) continue;
      out.set(uid, {
        component,
        gen: g + shift,
        parents: parents.get(pid),
        isParent: isParent.has(pid),
        hasPartner: hasPartner.has(pid)
      });
    }
  });
  return out;
};

// Did the edit change this person's place in the family? See the header.
const movedStructurally = (before, after) =>
  before.component !== after.component ||
  before.gen !== after.gen ||
  (before.parents.length > 0 &&
    !before.parents.some((p) => after.parents.includes(p))) ||
  (!before.isParent && after.isParent && !before.hasPartner);

// The anchor both sides are measured from: the proband when it is present
// before AND after the edit, else the first person (in the after order) who is.
// Any person present on both sides gives the same answer for everyone whose
// place did not change; the proband is just the natural choice.
const resolveAnchor = (before, after, probandUid) => {
  const uidsBefore = new Set(
    (before || []).map((m) => idKey(m?.uid)).filter((u) => u != null)
  );
  const inBoth = (uid) =>
    uid != null &&
    uidsBefore.has(uid) &&
    (after || []).some((m) => idKey(m?.uid) === uid);
  if (inBoth(idKey(probandUid))) return idKey(probandUid);
  const first = (after || []).find((m) => uidsBefore.has(idKey(m?.uid)));
  return first ? idKey(first.uid) : null;
};

/**
 * The stored order with the rank of everyone the edit moved removed (see the
 * header for what "moved" means). Call it with the members just before and just
 * after ONE structural edit, in the same state update as the edit.
 * @param {Map<string|number, number>} order  uid -> rank (establishedOrder)
 * @param {object[]} beforeMembers
 * @param {object[]} afterMembers
 * @param {string|number|null} probandUid
 * @returns {Map} `order` itself when nothing is dropped, else a new Map
 */
export const pruneRanksForStructuralEdit = (
  order,
  beforeMembers,
  afterMembers,
  probandUid
) => {
  if (!(order instanceof Map) || order.size === 0) return order;
  const anchor = resolveAnchor(beforeMembers, afterMembers, probandUid);
  const before = structuralPositions(beforeMembers, anchor);
  const after = structuralPositions(afterMembers, anchor);
  const stale = [];
  for (const key of order.keys()) {
    const was = before.get(idKey(key));
    if (!was) continue;
    const now = after.get(idKey(key));
    if (!now || movedStructurally(was, now)) stale.push(key);
  }
  if (!stale.length) return order;
  const next = new Map(order);
  for (const key of stale) next.delete(key);
  return next;
};
